/**
 * @param {object} settings
 * @param {number} settings.startPosition
 * @param {number} settings.desiredPosition
 * @param {number} settings.velocity
 * @param {function(number) : void} settings.update
 */
function smoothAnimation({ startPosition, desiredPosition, velocity, update }) {
  let lastTimestamp
  let lastPosition = startPosition

  function step(timestamp) {
    if (lastTimestamp === undefined) {
      lastTimestamp = timestamp
    }

    const deltaTime = timestamp - lastTimestamp

    const newPosition = Math.min(lastPosition + deltaTime * velocity, desiredPosition)

    update(newPosition)

    if (newPosition < desiredPosition) {
      lastTimestamp = timestamp
      lastPosition = newPosition

      requestAnimationFrame(step)
    }
  }

  requestAnimationFrame(step)
}

/**
 * @typedef ToastSettings
 * @type {object}
 * @property {number} duration - ms
 * @property {string} text - text content
 * @property {string} gravity - 'top' or 'bottom'
 * @property {string} position - 'left', 'center' or 'right'
 * @property {string} color
 * @property {string} backgroundColor
 * @property {object} offset
 * @property {number} offset.x
 * @property {number} offset.y
 */

export default class Toast {
  /**
   * @param {ToastSettings} generatorSettings
   * @returns {function(ToastSettings) : Toast}
   */
  static generator(generatorSettings) {
    /**
     * @param {ToastSettings} settings
     * @returns {Toast}
     */
    function construct(settings) {
      return new Toast({ ...generatorSettings, ...settings })
    }

    return construct
  }

  /**
   * Creates an instance of Toast.
   * @param {ToastSettings} settings
   * @memberof Toast
   */
  constructor({ text, duration, gravity, position, color, backgroundColor, offset }) {
    this.text = text
    this.duration = duration
    this.gravity = gravity
    this.position = position
    this.color = color
    this.backgroundColor = backgroundColor
    this.offset = offset
  }

  build() {
    const element = document.createElement("div")
    element.appendChild(document.createTextNode(this.text))

    element.style.setProperty("position", "fixed")

    if (this.gravity === "top") {
      smoothAnimation({
        startPosition: -20,
        desiredPosition: this.offset.y,
        velocity: 0.1,
        update(position) {
          element.style.setProperty("top", `${position}px`)
        },
      })
    }
    if (this.gravity === "bottom") {
      smoothAnimation({
        startPosition: -20,
        desiredPosition: this.offset.y,
        velocity: 0.1,
        update(position) {
          element.style.setProperty("bottom", `${position}px`)
        },
      })
    }

    if (this.position === "left") {
      element.style.setProperty("left", `${0 + this.offset.x}px`)
    }
    if (this.position === "center") {
      element.style.setProperty("left", "50%")
      element.style.setProperty("transform", "translateX(-50%)")
    }
    if (this.position === "right") {
      element.style.setProperty("right", `${0 + this.offset.x}px`)
    }

    element.style.setProperty("padding", "0.5rem 1rem")
    element.style.setProperty("font-size", "1rem")
    element.style.setProperty("line-height", 1.5)
    element.style.setProperty("color", this.color)
    element.style.setProperty("text-align", "center")
    element.style.setProperty("background-color", this.backgroundColor)
    element.style.setProperty("box-shadow", "1px 1px 5px 1px hsla(0deg, 0%, 0%, 0.5)")

    document.body.appendChild(element)

    setTimeout(() => {
      element.remove()
    }, this.duration)
  }
}
